import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { uploadImageToCloudinary, validateImageFile, uploadVideoToCloudinary, validateVideoFile } from "../Utils/upload";
import "./AdminInventory.css";

export default function AdminInventory({ api, categories = [], addNotification }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [stockFilter, setStockFilter] = useState("all"); // "all" | "in-stock" | "low-stock" | "out-of-stock"
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Pagination State (10, 20, 30, 40, 50 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  // Drawer / Modal Edit State
  const [editingProduct, setEditingProduct] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const editFileInputRef = useRef(null);
  const editVideoInputRef = useRef(null);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // 1. Fetch Products
  const fetchInventory = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/products");
      const data = res.data?.data || res.data || [];
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load inventory:", err);
      setError("Failed to load products inventory. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  // 2. Computed Inventory Statistics
  const stats = useMemo(() => {
    let totalUnits = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;

    products.forEach((p) => {
      const sizes = p.productSize || [];
      const productTotal = sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
      totalUnits += productTotal;

      const hasOutOfStock = sizes.some((s) => (Number(s.stock) || 0) === 0);
      const hasLowStock = sizes.some((s) => (Number(s.stock) || 0) > 0 && (Number(s.stock) || 0) <= 3);

      if (hasOutOfStock || sizes.length === 0) outOfStockCount++;
      if (hasLowStock) lowStockCount++;
    });

    return {
      totalProducts: products.length,
      totalUnits,
      lowStockCount,
      outOfStockCount,
    };
  }, [products]);

  // 3. Unified Search & Filtering Logic
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const q = searchQuery.toLowerCase().trim();

      // Search matching (Name, ID/SKU, Tags, Category, Subcategory)
      if (q) {
        const nameMatch = (product.productName || "").toLowerCase().includes(q);
        const idMatch = String(product.productId || "").toLowerCase().includes(q);
        const descMatch = (product.description || "").toLowerCase().includes(q);
        const tagMatch = (product.tags || []).some((t) => (t || "").toLowerCase().includes(q));
        const catMatch = (product.productCategory || []).some((c) => {
          const cName = (c.categoryName || "").toLowerCase();
          const scName = (c.subCategory?.categoryName || "").toLowerCase();
          return cName.includes(q) || scName.includes(q);
        });

        if (!nameMatch && !idMatch && !descMatch && !tagMatch && !catMatch) {
          return false;
        }
      }

      // Category Filter
      if (selectedCategory !== "all") {
        const hasCategory = (product.productCategory || []).some(
          (c) =>
            (c.categoryName || "").toLowerCase() === selectedCategory.toLowerCase()
        );
        if (!hasCategory) return false;
      }

      // Price Filter
      const price = Number(product.productPrice) || 0;
      if (minPrice !== "" && price < Number(minPrice)) return false;
      if (maxPrice !== "" && price > Number(maxPrice)) return false;

      // Stock Filter
      const sizes = product.productSize || [];
      const totalStock = sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);

      if (stockFilter === "out-of-stock") {
        if (totalStock > 0 && !sizes.some((s) => (Number(s.stock) || 0) === 0)) return false;
      } else if (stockFilter === "low-stock") {
        if (!sizes.some((s) => (Number(s.stock) || 0) > 0 && (Number(s.stock) || 0) <= 3)) return false;
      } else if (stockFilter === "in-stock") {
        if (totalStock === 0) return false;
      }

      return true;
    });
  }, [products, searchQuery, selectedCategory, minPrice, maxPrice, stockFilter]);

  // 4. Retail POS Quick Stock Update (Optimistic with Rollback)
  const handleUpdateStock = async (productId, sizeName, newStock) => {
    const validStock = Math.max(0, parseInt(newStock, 10) || 0);

    // Save previous product state for rollback
    let previousProduct = null;
    setProducts((prev) =>
      prev.map((p) => {
        if (p.productId === productId) {
          previousProduct = JSON.parse(JSON.stringify(p));
          const updatedSizes = (p.productSize || []).map((sz) =>
            sz.sizeName?.toLowerCase() === sizeName?.toLowerCase()
              ? { ...sz, stock: validStock }
              : sz
          );
          return { ...p, productSize: updatedSizes };
        }
        return p;
      })
    );

    // Call API with fallback support
    try {
      try {
        await api.patch(`/products/${productId}/stock`, {
          sizeName,
          stock: validStock,
        });
      } catch (patchErr) {
        if (previousProduct) {
          const updatedSizes = (previousProduct.productSize || []).map((sz) =>
            sz.sizeName?.toLowerCase() === sizeName?.toLowerCase()
              ? { ...sz, stock: validStock }
              : sz
          );
          await api.put(`/products/${productId}`, {
            ...previousProduct,
            productSize: updatedSizes,
          });
        }
      }
    } catch (err) {
      console.error(`Failed to update stock for product ${productId}:`, err);
      if (previousProduct) {
        setProducts((prev) =>
          prev.map((p) => (p.productId === productId ? previousProduct : p))
        );
      }
      if (addNotification) {
        addNotification({
          type: "error",
          title: "Stock Update Failed",
          message: `Could not update size ${sizeName} for product #${productId}. Rolled back.`,
        });
      }
    }
  };

  // Helper to extract clean category data
  const getCleanCategories = (product) => {
    let catName = "BASICS";
    let subCatName = "General";

    if (product?.productCategory) {
      if (Array.isArray(product.productCategory) && product.productCategory.length > 0) {
        const first = product.productCategory[0];
        if (first?.categoryName && first.categoryName.trim()) {
          catName = first.categoryName.trim();
        }
        if (first?.subCategory?.categoryName && first.subCategory.categoryName.trim()) {
          subCatName = first.subCategory.categoryName.trim();
        } else if (typeof first?.subCategory === "string" && first.subCategory.trim()) {
          subCatName = first.subCategory.trim();
        }
      } else if (typeof product.productCategory === "object") {
        if (product.productCategory.categoryName && product.productCategory.categoryName.trim()) {
          catName = product.productCategory.categoryName.trim();
        }
        if (product.productCategory.subCategory?.categoryName && product.productCategory.subCategory.categoryName.trim()) {
          subCatName = product.productCategory.subCategory.categoryName.trim();
        }
      }
    }

    return [{ categoryName: catName, subCategory: { categoryName: subCatName } }];
  };

  // 5. Drawer Edit Handlers
  const handleOpenEdit = (product) => {
    setEditingProduct(product);
    setEditFormData({
      productId: product.productId,
      productName: product.productName || "",
      productPrice: product.productPrice || "",
      beforeDiscountPrice: product.beforeDiscountPrice || "",
      description: product.description || "",
      brand: product.brand || "Aminii",
      status: product.status || "active",
      productDate: product.productDate || new Date().toLocaleDateString("en-GB").split("/").join("-"),
      productImages: [...(product.productImages || [])],
      productVideo: product.productVideo || product.videoUrl || "",
      productCategory: getCleanCategories(product),
      productSize: [...(product.productSize || [{ sizeName: "S", stock: 10 }])],
      tags: [...(product.tags || [])],
    });
  };

  const handleCloseEdit = () => {
    setEditingProduct(null);
    setEditFormData(null);
  };

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditCategoryChange = (e) => {
    const catName = e.target.value;
    setEditFormData((prev) => {
      const currentSub = prev.productCategory?.[0]?.subCategory?.categoryName || "General";
      return {
        ...prev,
        productCategory: [
          {
            categoryName: catName || "BASICS",
            subCategory: { categoryName: currentSub },
          },
        ],
      };
    });
  };

  const handleEditSubCategoryChange = (e) => {
    const subCatName = e.target.value;
    setEditFormData((prev) => {
      const currentCat = prev.productCategory?.[0]?.categoryName || "BASICS";
      return {
        ...prev,
        productCategory: [
          {
            categoryName: currentCat,
            subCategory: { categoryName: subCatName || "General" },
          },
        ],
      };
    });
  };

  const handleEditSizeChange = (index, field, value) => {
    const updated = [...editFormData.productSize];
    updated[index] = {
      ...updated[index],
      [field]: field === "stock" ? Math.max(0, parseInt(value, 10) || 0) : value,
    };
    setEditFormData((prev) => ({ ...prev, productSize: updated }));
  };

  const handleAddEditSize = () => {
    const existing = editFormData.productSize.map((s) => s.sizeName?.toUpperCase());
    const commonSizes = ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL", "6XL", "FREE SIZE"];
    const nextSize = commonSizes.find((s) => !existing.includes(s)) || "NEW";
    setEditFormData((prev) => ({
      ...prev,
      productSize: [...prev.productSize, { sizeName: nextSize, stock: 10 }],
    }));
  };

  const handleRemoveEditSize = (index) => {
    if (editFormData.productSize.length <= 1) return;
    setEditFormData((prev) => ({
      ...prev,
      productSize: prev.productSize.filter((_, i) => i !== index),
    }));
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      if (addNotification) {
        addNotification({ type: "error", title: "Image Error", message: validation.error });
      }
      return;
    }

    try {
      setIsUploadingImage(true);
      const url = await uploadImageToCloudinary(file);
      setEditFormData((prev) => ({
        ...prev,
        productImages: [...prev.productImages, url],
      }));
    } catch (err) {
      if (addNotification) {
        addNotification({ type: "error", title: "Upload Failed", message: err.message });
      }
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleUploadVideo = async (file) => {
    if (!file) return;
    const validation = validateVideoFile(file);
    if (!validation.valid) {
      if (addNotification) {
        addNotification({ type: "error", title: "Video Error", message: validation.error });
      }
      return;
    }
    try {
      setIsUploadingVideo(true);
      const url = await uploadVideoToCloudinary(file, () => {});
      setEditFormData((prev) => ({ ...prev, productVideo: url }));
      if (addNotification) {
        addNotification({ type: "success", title: "Video Uploaded", message: "Product video uploaded successfully." });
      }
    } catch (err) {
      if (addNotification) {
        addNotification({ type: "error", title: "Upload Failed", message: err.message });
      }
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleRemoveEditVideo = () => {
    setEditFormData((prev) => ({ ...prev, productVideo: "" }));
  };

  const handleRemoveImage = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      productImages: prev.productImages.filter((_, i) => i !== index),
    }));
  };

  const handleSetPrimaryImage = (index) => {
    if (index === 0) return;
    setEditFormData((prev) => {
      const imgs = [...prev.productImages];
      const [chosen] = imgs.splice(index, 1);
      imgs.unshift(chosen);
      return { ...prev, productImages: imgs };
    });
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!editFormData || isSaving) return;

    try {
      setIsSaving(true);

      const rawCategories = editFormData.productCategory;
      const cleanCats = (Array.isArray(rawCategories) && rawCategories.length > 0
        ? rawCategories
        : [{ categoryName: "BASICS", subCategory: { categoryName: "General" } }]
      ).map((c) => ({
        categoryName: (c?.categoryName && String(c.categoryName).trim()) || "BASICS",
        subCategory: {
          categoryName: (c?.subCategory?.categoryName && String(c.subCategory.categoryName).trim()) || "General"
        }
      }));

      const payload = {
        ...editFormData,
        productPrice: parseFloat(editFormData.productPrice),
        beforeDiscountPrice: editFormData.beforeDiscountPrice
          ? parseFloat(editFormData.beforeDiscountPrice)
          : null,
        productCategory: cleanCats,
      };

      const res = await api.put(`/products/${editFormData.productId}`, payload);
      const saved = res.data?.data || res.data || payload;

      setProducts((prev) =>
        prev.map((p) => (p.productId === editFormData.productId ? saved : p))
      );

      if (addNotification) {
        addNotification({
          type: "success",
          title: "Product Updated",
          message: `Product #${editFormData.productId} updated successfully.`,
        });
      }
      handleCloseEdit();
    } catch (err) {
      console.error("Failed to save product:", err);
      if (addNotification) {
        addNotification({
          type: "error",
          title: "Save Failed",
          message: err.response?.data?.message || err.message || "Could not update product.",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!editFormData) return;
    if (!window.confirm(`Are you sure you want to permanently delete product #${editFormData.productId}?`)) {
      return;
    }

    try {
      setIsSaving(true);
      await api.delete(`/products/${editFormData.productId}`);
      setProducts((prev) => prev.filter((p) => p.productId !== editFormData.productId));

      if (addNotification) {
        addNotification({
          type: "success",
          title: "Product Deleted",
          message: `Product #${editFormData.productId} removed from catalogue.`,
        });
      }
      handleCloseEdit();
    } catch (err) {
      console.error("Delete failed:", err);
      if (addNotification) {
        addNotification({
          type: "error",
          title: "Delete Failed",
          message: err.response?.data?.message || "Could not delete product.",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const activeFilterCount = [searchQuery, selectedCategory !== "all", minPrice, maxPrice, stockFilter !== "all"].filter(Boolean).length;

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setMinPrice("");
    setMaxPrice("");
    setStockFilter("all");
    setCurrentPage(1);
  };

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, minPrice, maxPrice, stockFilter, pageSize]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginatedProducts = useMemo(() => {
    const startIdx = (validCurrentPage - 1) * pageSize;
    return filteredProducts.slice(startIdx, startIdx + pageSize);
  }, [filteredProducts, validCurrentPage, pageSize]);

  // Export all products to Excel (CSV format with BOM for universal Excel compatibility)
  const handleExportExcel = () => {
    try {
      setIsExporting(true);
      const headers = [
        "Product ID",
        "Product Name",
        "Category",
        "Sub Category",
        "Selling Price (INR)",
        "Original Price (INR)",
        "Status",
        "Brand",
        "Total Stock Units",
        "Size Variants & Stock",
        "Tags",
        "Cover Image URL",
        "Description"
      ];

      const csvRows = [];
      csvRows.push(headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(","));

      filteredProducts.forEach((p) => {
        const sizes = p.productSize || [];
        const totalUnits = sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
        const sizeBreakdown = sizes.map((s) => `${s.sizeName}: ${s.stock}`).join(" | ");
        const catName = p.productCategory?.[0]?.categoryName || "N/A";
        const subCatName = p.productCategory?.[0]?.subCategory?.categoryName || "N/A";
        const coverImg = p.productImages?.[0] || "";
        const tags = (p.tags || []).join(", ");
        const desc = (p.description || "").replace(/(\r\n|\n|\r)/gm, " ");

        const row = [
          p.productId || "",
          p.productName || "",
          catName,
          subCatName,
          p.productPrice || 0,
          p.beforeDiscountPrice || "",
          p.status || "active",
          p.brand || "Aminii",
          totalUnits,
          sizeBreakdown,
          tags,
          coverImg,
          desc
        ];

        csvRows.push(
          row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")
        );
      });

      const csvString = "\uFEFF" + csvRows.join("\r\n"); // UTF-8 BOM for Microsoft Excel
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const dateStr = new Date().toISOString().split("T")[0];
      link.setAttribute("href", url);
      link.setAttribute("download", `Aminii_Products_Inventory_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (addNotification) {
        addNotification({
          type: "success",
          title: "Export Successful",
          message: `Exported ${filteredProducts.length} products to Excel CSV file.`
        });
      }
    } catch (err) {
      console.error("Export failed:", err);
      if (addNotification) {
        addNotification({
          type: "error",
          title: "Export Failed",
          message: "Failed to generate Excel export file."
        });
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="admin-inventory-container">

      {/* ── MOBILE LAYOUT ── */}
      {isMobile ? (
        <>
          {/* Mobile Stats Chips Row */}
          <div className="mob-stats-row">
            <div className="mob-stat-chip total">
              <span className="mob-stat-num">{stats.totalProducts}</span>
              <span className="mob-stat-lbl">Styles</span>
            </div>
            <div className="mob-stat-chip stock">
              <span className="mob-stat-num">{stats.totalUnits}</span>
              <span className="mob-stat-lbl">Units</span>
            </div>
            <div className="mob-stat-chip low">
              <span className="mob-stat-num" style={{ color: "#fbbf24" }}>{stats.lowStockCount}</span>
              <span className="mob-stat-lbl">Low</span>
            </div>
            <div className="mob-stat-chip out">
              <span className="mob-stat-num" style={{ color: "#f87171" }}>{stats.outOfStockCount}</span>
              <span className="mob-stat-lbl">OOS</span>
            </div>
          </div>

          {/* Mobile Search + Filter FAB */}
          <div className="mob-search-bar">
            <div className="inv-search-input-wrap" style={{ flex: 1 }}>
              <span className="inv-search-icon">🔍</span>
              <input
                type="text"
                className="inv-search-input"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="inv-clear-search" onClick={() => setSearchQuery("")} aria-label="Clear">✕</button>
              )}
            </div>
            <button
              className={`mob-filter-fab ${showFilterPanel ? "active" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`}
              onClick={() => setShowFilterPanel((p) => !p)}
              aria-label="Filters"
            >
              <span>⚙</span>
              {activeFilterCount > 0 && <span className="mob-filter-badge">{activeFilterCount}</span>}
            </button>
          </div>

          {/* Mobile Filter Panel — Collapsible */}
          {showFilterPanel && (
            <div className="mob-filter-panel">
              {/* Stock Status Tabs */}
              <div className="mob-filter-section">
                <span className="mob-filter-section-label">Stock Status</span>
                <div className="mob-stock-tabs">
                  {[
                    { label: "All", val: "all", icon: "📦" },
                    { label: "In Stock", val: "in-stock", icon: "✅" },
                    { label: "Low (≤3)", val: "low-stock", icon: "⚠️" },
                    { label: "OOS", val: "out-of-stock", icon: "❌" },
                  ].map((tab) => (
                    <button
                      key={tab.val}
                      type="button"
                      className={`mob-stock-tab ${stockFilter === tab.val ? "active" : ""}`}
                      onClick={() => setStockFilter(tab.val)}
                    >
                      <span>{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div className="mob-filter-section">
                <span className="mob-filter-section-label">Category</span>
                <select
                  className="inv-select"
                  style={{ width: "100%" }}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map((c, i) => (
                    <option key={i} value={c.categoryName}>{c.categoryName}</option>
                  ))}
                </select>
              </div>

              {/* Price Range */}
              <div className="mob-filter-section">
                <span className="mob-filter-section-label">Price Range (₹)</span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <input type="number" placeholder="Min" className="inv-search-input" style={{ padding: "10px 12px" }} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
                  <input type="number" placeholder="Max" className="inv-search-input" style={{ padding: "10px 12px" }} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
                </div>
              </div>

              {/* Filter Actions */}
              <div className="mob-filter-actions">
                {activeFilterCount > 0 && (
                  <button className="mob-reset-btn" onClick={resetFilters}>Reset All</button>
                )}
                <button className="mob-apply-btn" onClick={() => setShowFilterPanel(false)}>
                  Show {filteredProducts.length} Items
                </button>
              </div>
            </div>
          )}

          {/* Mobile Results Count & Export */}
          <div className="mob-results-count" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <span>
              Showing <strong>{filteredProducts.length === 0 ? 0 : (validCurrentPage - 1) * pageSize + 1}–{Math.min(validCurrentPage * pageSize, filteredProducts.length)}</strong> of {filteredProducts.length} products
            </span>
            <button
              type="button"
              className="inv-export-btn"
              onClick={handleExportExcel}
              disabled={isExporting || filteredProducts.length === 0}
              title="Export all filtered products to Excel CSV"
            >
              📥 {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>

          {/* Mobile Product Cards */}
          <div className="mob-product-list">
            {loading ? (
              <div className="inv-empty-state"><div className="inv-empty-icon">⏳</div><p>Loading inventory...</p></div>
            ) : error ? (
              <div className="inv-empty-state" style={{ color: "#f87171" }}><p>{error}</p><button className="inv-edit-btn" onClick={fetchInventory}>Retry</button></div>
            ) : paginatedProducts.length === 0 ? (
              <div className="inv-empty-state"><div className="inv-empty-icon">🔎</div><p>No products found.</p></div>
            ) : (
              paginatedProducts.map((product) => {
                const sizes = product.productSize || [];
                const totalUnits = sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
                const firstImg = product.productImages?.[0] || "https://placehold.co/100x130?text=No+Image";
                const catName = product.productCategory?.[0]?.subCategory?.categoryName || product.productCategory?.[0]?.categoryName || "General";
                const isExpanded = expandedCardId === product.productId;

                return (
                  <div key={product.productId} className={`mob-product-card`}>
                    <div className="mob-card-top">
                      <div className="mob-card-img-wrap">
                        <img
                          src={firstImg}
                          alt={product.productName}
                          className="mob-card-img"
                          onError={(e) => { e.target.src = "https://placehold.co/100x130?text=No+Image"; }}
                        />
                      </div>
                      <div className="mob-card-info" onClick={() => handleOpenEdit(product)}>
                        <h4 className="mob-card-title">{product.productName || "Unnamed Product"}</h4>
                        <div className="mob-card-meta">
                          <span className="mob-card-sku">ID: #{product.productId}</span>
                          <span className="mob-card-dot">•</span>
                          <span className="mob-card-cat">{catName}</span>
                        </div>
                        <div className="mob-card-prices">
                          <span className="mob-card-price">₹{product.productPrice}</span>
                          {product.beforeDiscountPrice && (
                            <span className="mob-card-orig-price">₹{product.beforeDiscountPrice}</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mob-edit-icon-btn"
                        onClick={() => handleOpenEdit(product)}
                        aria-label="Edit product"
                      >
                        ✏️
                      </button>
                    </div>

                    {/* Stock Summary Bar */}
                    <div
                      className="mob-stock-summary-bar"
                      onClick={() => setExpandedCardId(isExpanded ? null : product.productId)}
                    >
                      <div className="mob-stock-pills-row">
                        {sizes.length === 0 ? (
                          <span className="mob-no-sizes">No sizes configured</span>
                        ) : (
                          sizes.map((sz, i) => {
                            const stockQty = Number(sz.stock) || 0;
                            const isOOS = stockQty === 0;
                            const isLow = stockQty > 0 && stockQty <= 3;
                            return (
                              <span
                                key={i}
                                className={`mob-mini-size-pill ${isOOS ? "oos" : isLow ? "low" : ""}`}
                              >
                                {sz.sizeName}: <strong>{stockQty}</strong>
                              </span>
                            );
                          })
                        )}
                      </div>
                      <div className="mob-stock-summary-right">
                        <span className={`mob-total-units ${totalUnits === 0 ? "oos" : totalUnits <= 5 ? "low" : ""}`}>
                          {totalUnits} units
                        </span>
                        <span className={`mob-chevron ${isExpanded ? "open" : ""}`}>▼</span>
                      </div>
                    </div>

                    {/* Expandable POS Stock Controls */}
                    {isExpanded && (
                      <div className="mob-pos-section">
                        <div className="mob-pos-header">
                          <span className="mob-pos-label">Quick POS Stock Controls</span>
                          <span className="mob-pos-hint">Tap + / − or OOS to update live</span>
                        </div>
                        <div className="mob-size-stepper-list">
                          {sizes.length === 0 ? (
                            <p className="mob-no-sizes" style={{ padding: "8px 0" }}>No sizes to manage.</p>
                          ) : (
                            sizes.map((sz, i) => {
                              const stockQty = Number(sz.stock) || 0;
                              const isOOS = stockQty === 0;
                              const isLow = stockQty > 0 && stockQty <= 3;
                              return (
                                <div key={i} className="mob-size-stepper-row">
                                  <span className="mob-stepper-size-name">{sz.sizeName}</span>
                                  <div className="mob-stepper-controls">
                                    <button
                                      type="button"
                                      className="mob-qty-btn minus"
                                      onClick={() => handleUpdateStock(product.productId, sz.sizeName, stockQty - 1)}
                                      disabled={isOOS}
                                    >−</button>
                                    <span className={`mob-qty-display ${isOOS ? "oos" : isLow ? "low" : ""}`}>{stockQty}</span>
                                    <button
                                      type="button"
                                      className="mob-qty-btn plus"
                                      onClick={() => handleUpdateStock(product.productId, sz.sizeName, stockQty + 1)}
                                    >+</button>
                                    {!isOOS ? (
                                      <button
                                        type="button"
                                        className="mob-oos-btn"
                                        onClick={() => handleUpdateStock(product.productId, sz.sizeName, 0)}
                                        title="Set Out of Stock"
                                      >OOS</button>
                                    ) : (
                                      <span className="mob-oos-tag">Sold Out</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Mobile Pagination Controls */}
          {filteredProducts.length > 0 && (
            <div className="inv-pagination-bar mob">
              <div className="inv-page-size-wrap">
                <label htmlFor="mob-page-size">Per Page:</label>
                <select
                  id="mob-page-size"
                  className="inv-page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 30, 40, 50].map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              <div className="inv-page-nav">
                <button
                  type="button"
                  className="inv-page-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={validCurrentPage === 1}
                  title="First Page"
                >«</button>
                <button
                  type="button"
                  className="inv-page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={validCurrentPage === 1}
                  title="Previous Page"
                >‹</button>
                <span className="inv-page-indicator">
                  Page {validCurrentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="inv-page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={validCurrentPage === totalPages}
                  title="Next Page"
                >›</button>
                <button
                  type="button"
                  className="inv-page-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={validCurrentPage === totalPages}
                  title="Last Page"
                >»</button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* ── DESKTOP LAYOUT (unchanged) ── */}
          {/* 1. Inventory Metric Stats */}
          <div className="inventory-summary-bar">
            <div className="inv-stat-card">
              <div className="inv-stat-info">
                <span className="inv-stat-value">{stats.totalProducts}</span>
                <span className="inv-stat-label">Total Styles</span>
              </div>
            </div>
            <div className="inv-stat-card">
              <div className="inv-stat-info">
                <span className="inv-stat-value">{stats.totalUnits}</span>
                <span className="inv-stat-label">Stock Units</span>
              </div>
            </div>
            <div className="inv-stat-card">
              <div className="inv-stat-info">
                <span className="inv-stat-value" style={{ color: "#fbbf24" }}>{stats.lowStockCount}</span>
                <span className="inv-stat-label">Low Stock (≤ 3)</span>
              </div>
            </div>
            <div className="inv-stat-card">
              <div className="inv-stat-info">
                <span className="inv-stat-value" style={{ color: "#f87171" }}>{stats.outOfStockCount}</span>
                <span className="inv-stat-label">Out of Stock</span>
              </div>
            </div>
          </div>

          {/* 2. Unified Search & Filtering Toolbar */}
          <div className="inventory-toolbar-card">
            <div className="inv-search-row">
              <div className="inv-search-input-wrap">
                <span className="inv-search-icon">🔍</span>
                <input
                  type="text"
                  className="inv-search-input"
                  placeholder="Search by title, SKU / ID, category, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="inv-clear-search" onClick={() => setSearchQuery("")} aria-label="Clear search">✕</button>
                )}
              </div>
              <div className="inv-status-tabs">
                {[
                  { label: "All Products", val: "all" },
                  { label: "In Stock", val: "in-stock" },
                  { label: "Low Stock (≤ 3)", val: "low-stock" },
                  { label: "Out of Stock (0)", val: "out-of-stock" },
                ].map((tab) => (
                  <button
                    key={tab.val}
                    type="button"
                    className={`inv-status-tab ${stockFilter === tab.val ? "active" : ""}`}
                    onClick={() => setStockFilter(tab.val)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="inv-filters-row">
              <select className="inv-select" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {categories.map((c, i) => <option key={i} value={c.categoryName}>{c.categoryName}</option>)}
              </select>
              <div className="inv-price-range">
                <span style={{ fontSize: "11px", color: "#a0a0aa", fontWeight: 700, textTransform: "uppercase" }}>Price ₹:</span>
                <input type="number" placeholder="Min" className="inv-price-input" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
                <span style={{ color: "#71717a" }}>–</span>
                <input type="number" placeholder="Max" className="inv-price-input" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
              </div>
              {activeFilterCount > 0 && (
                <button style={{ background: "none", border: "none", color: "#f87171", fontSize: "12px", fontWeight: 600, cursor: "pointer", padding: "6px 8px", textDecoration: "underline" }} onClick={resetFilters}>
                  Reset Filters
                </button>
              )}

              {/* Per Page Selector & Export Excel Button */}
              <div className="inv-toolbar-actions-right">
                <div className="inv-per-page-selector">
                  <label htmlFor="inv-page-size-select">Per Page:</label>
                  <select
                    id="inv-page-size-select"
                    className="inv-page-size-dropdown"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {[10, 20, 30, 40, 50].map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  className="inv-export-btn"
                  onClick={handleExportExcel}
                  disabled={isExporting || filteredProducts.length === 0}
                  title="Export all filtered products to Excel CSV format"
                >
                  📥 {isExporting ? "Exporting..." : "Export Excel"}
                </button>

                <div className="inv-items-count-display">
                  Showing <strong>{filteredProducts.length === 0 ? 0 : (validCurrentPage - 1) * pageSize + 1}–{Math.min(validCurrentPage * pageSize, filteredProducts.length)}</strong> of {filteredProducts.length} items
                </div>
              </div>
            </div>
          </div>

          {/* 3. Desktop Inventory Table */}
          <div className="inventory-list-card">
            {loading ? (
              <div className="inv-empty-state"><div className="inv-empty-icon">⏳</div><p>Loading inventory catalogue...</p></div>
            ) : error ? (
              <div className="inv-empty-state" style={{ color: "#f87171" }}><p>{error}</p><button className="inv-edit-btn" onClick={fetchInventory}>Retry</button></div>
            ) : filteredProducts.length === 0 ? (
              <div className="inv-empty-state"><div className="inv-empty-icon">🔎</div><p>No products match your search or filter criteria.</p></div>
            ) : (
              <div className="inventory-table-container">
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Price</th>
                      <th>Size Variants (Retail POS Stock Controls)</th>
                      <th>Total</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.map((product) => {
                      const sizes = product.productSize || [];
                      const totalUnits = sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
                      const firstImg = product.productImages?.[0] || "https://placehold.co/100x130?text=No+Image";
                      const catName = product.productCategory?.[0]?.categoryName || "General";

                      return (
                        <tr key={product.productId} className="inventory-row">
                          <td>
                            <div className="inv-prod-cell">
                              <div className="inv-thumb-wrap">
                                <img src={firstImg} alt={product.productName} className="inv-thumb-img" onError={(e) => { e.target.src = "https://placehold.co/100x130?text=No+Image"; }} />
                              </div>
                              <div className="inv-prod-meta">
                                <span className="inv-prod-title" onClick={() => handleOpenEdit(product)} title="Click to edit">{product.productName || "Unnamed Product"}</span>
                                <span className="inv-prod-sku">ID: #{product.productId}</span>
                                <span className="inv-prod-cat">{catName}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="inv-price-cell">
                              <span className="inv-sale-price">₹{product.productPrice}</span>
                              {product.beforeDiscountPrice && <span className="inv-orig-price">₹{product.beforeDiscountPrice}</span>}
                            </div>
                          </td>
                          <td>
                            <div className="inv-sizes-matrix">
                              {sizes.length === 0 ? (
                                <span style={{ fontSize: "12px", color: "#71717a" }}>No size variants configured</span>
                              ) : (
                                sizes.map((sz, i) => {
                                  const stockQty = Number(sz.stock) || 0;
                                  const isOOS = stockQty === 0;
                                  const isLow = stockQty > 0 && stockQty <= 3;
                                  return (
                                    <div key={i} className={`inv-size-pill ${isOOS ? "out-of-stock" : isLow ? "low-stock" : ""}`}>
                                      <span className="inv-size-name">{sz.sizeName}</span>
                                      <button type="button" className="inv-qty-btn" onClick={() => handleUpdateStock(product.productId, sz.sizeName, stockQty - 1)} disabled={isOOS} title="Decrease">−</button>
                                      <span className="inv-qty-val">{stockQty}</span>
                                      <button type="button" className="inv-qty-btn" onClick={() => handleUpdateStock(product.productId, sz.sizeName, stockQty + 1)} title="Increase">+</button>
                                      {!isOOS ? (
                                        <button type="button" className="inv-oos-btn" onClick={() => handleUpdateStock(product.productId, sz.sizeName, 0)} title="Mark OOS">OOS</button>
                                      ) : (
                                        <span className="inv-oos-badge">Sold Out</span>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`inv-total-stock-badge ${totalUnits === 0 ? "empty" : totalUnits <= 5 ? "low" : "healthy"}`}>
                              {totalUnits === 0 ? "0 Units" : `${totalUnits} Units`}
                            </span>
                          </td>
                          <td className="inv-actions-cell">
                            <button type="button" className="inv-edit-btn" onClick={() => handleOpenEdit(product)}>✏️ Edit</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Desktop Pagination Controls Bar */}
            {filteredProducts.length > 0 && (
              <div className="inv-pagination-bar">
                <div className="inv-pagination-info">
                  Showing <strong>{(validCurrentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(validCurrentPage * pageSize, filteredProducts.length)}</strong> of <strong>{filteredProducts.length}</strong> products
                </div>

                <div className="inv-page-nav">
                  <button
                    type="button"
                    className="inv-page-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={validCurrentPage === 1}
                    title="First Page"
                  >
                    « First
                  </button>
                  <button
                    type="button"
                    className="inv-page-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={validCurrentPage === 1}
                    title="Previous Page"
                  >
                    ‹ Prev
                  </button>

                  <div className="inv-page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (validCurrentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (validCurrentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = validCurrentPage - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          type="button"
                          className={`inv-page-num-btn ${validCurrentPage === pageNum ? "active" : ""}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="inv-page-btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={validCurrentPage === totalPages}
                    title="Next Page"
                  >
                    Next ›
                  </button>
                  <button
                    type="button"
                    className="inv-page-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={validCurrentPage === totalPages}
                    title="Last Page"
                  >
                    Last »
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* 4. Edit Product Drawer / Modal - Rendered via React Portal */}
      {typeof document !== "undefined" &&
        editingProduct &&
        editFormData &&
        ReactDOM.createPortal(
          <div className="inv-drawer-backdrop" onClick={handleCloseEdit}>
            <div
              className="inv-drawer"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Header */}
              <div className="inv-drawer-header">
                    <div className="inv-drawer-title-wrap">
                      <h3 className="inv-drawer-title">Edit Product Details</h3>
                      <span className="inv-drawer-subtitle">
                        Product ID: #{editFormData.productId} • Live updates sync with storefront
                      </span>
                    </div>
                    <button
                      type="button"
                      className="inv-drawer-close"
                      onClick={handleCloseEdit}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Drawer Form Body */}
                  <form id="edit-product-form" className="inv-drawer-body" onSubmit={handleSaveProduct}>
                    {/* Media Section */}
                    <div className="inv-section-card">
                      <h4 className="inv-section-title">🖼️ Product Media ({editFormData.productImages.length}/5)</h4>
                      <div className="inv-gallery-grid">
                        {editFormData.productImages.map((img, i) => (
                          <div
                            key={i}
                            className={`inv-gallery-item ${i === 0 ? "primary" : ""}`}
                          >
                            <img src={img} alt={`Preview ${i + 1}`} />
                            <button
                              type="button"
                              className="inv-gallery-remove"
                              onClick={() => handleRemoveImage(i)}
                              title="Remove image"
                            >
                              ✕
                            </button>
                            {i === 0 ? (
                              <span className="inv-gallery-primary-tag">Primary</span>
                            ) : (
                              <button
                                type="button"
                                style={{
                                  position: "absolute",
                                  bottom: "4px",
                                  left: "4px",
                                  right: "4px",
                                  background: "rgba(15, 23, 42, 0.9)",
                                  color: "#f3e5ab",
                                  fontSize: "9px",
                                  fontWeight: "700",
                                  border: "1px solid rgba(212, 175, 55, 0.3)",
                                  borderRadius: "4px",
                                  padding: "2px 0",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleSetPrimaryImage(i)}
                              >
                                Set Primary
                              </button>
                            )}
                          </div>
                        ))}

                        {editFormData.productImages.length < 5 && (
                          <button
                            type="button"
                            className="inv-add-size-btn"
                            style={{ height: "100%", minHeight: "80px", flexDirection: "column" }}
                            onClick={() => editFileInputRef.current?.click()}
                            disabled={isUploadingImage}
                          >
                            {isUploadingImage ? "⏳..." : "+ Upload Image"}
                          </button>
                        )}
                      </div>
                      <input
                        type="file"
                        ref={editFileInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleUploadImage}
                      />
                    </div>

                    {/* Product Video Section */}
                    <div className="inv-section-card">
                      <h4 className="inv-section-title">🎬 Product Video <span style={{ fontSize: "11px", fontWeight: 400, color: "#71717a" }}>(Optional — appears as 2nd slide in gallery)</span></h4>
                      {editFormData.productVideo ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          <video
                            src={editFormData.productVideo}
                            controls
                            style={{ width: "100%", maxHeight: "200px", borderRadius: "8px", background: "#000", objectFit: "contain" }}
                          />
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              type="button"
                              className="inv-add-size-btn"
                              style={{ flex: 1 }}
                              disabled={isUploadingVideo}
                              onClick={() => editVideoInputRef.current?.click()}
                            >
                              {isUploadingVideo ? "⏳ Uploading…" : "🔄 Replace Video"}
                            </button>
                            <button
                              type="button"
                              className="inv-add-size-btn"
                              style={{ flex: 1, background: "rgba(239,68,68,0.1)", color: "#f87171" }}
                              onClick={handleRemoveEditVideo}
                            >
                              ✕ Remove Video
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="inv-add-size-btn"
                          style={{ width: "100%", minHeight: "64px", flexDirection: "column", gap: "4px" }}
                          disabled={isUploadingVideo}
                          onClick={() => editVideoInputRef.current?.click()}
                        >
                          {isUploadingVideo ? "⏳ Uploading video…" : <><span style={{ fontSize: "22px" }}>🎬</span><span>Upload Product Video</span><span style={{ fontSize: "11px", opacity: 0.6 }}>MP4, MOV, WebM — max 200 MB</span></>}
                        </button>
                      )}
                      <input
                        type="file"
                        ref={editVideoInputRef}
                        style={{ display: "none" }}
                        accept="video/mp4,video/mov,video/quicktime,video/webm"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadVideo(file);
                          e.target.value = "";
                        }}
                      />
                    </div>


                    {/* Title & Pricing */}
                    <div className="inv-section-card">
                      <h4 className="inv-section-title">💰 Pricing & Information</h4>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                          Product Name *
                        </label>
                        <input
                          type="text"
                          name="productName"
                          value={editFormData.productName}
                          onChange={handleEditFieldChange}
                          required
                          className="inv-search-input"
                          style={{ padding: "10px 14px" }}
                        />
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                            Sale Price (₹) *
                          </label>
                          <input
                            type="number"
                            name="productPrice"
                            step="0.01"
                            value={editFormData.productPrice}
                            onChange={handleEditFieldChange}
                            required
                            className="inv-search-input"
                            style={{ padding: "10px 14px" }}
                          />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                            Original Price (₹)
                          </label>
                          <input
                            type="number"
                            name="beforeDiscountPrice"
                            step="0.01"
                            value={editFormData.beforeDiscountPrice}
                            onChange={handleEditFieldChange}
                            className="inv-search-input"
                            style={{ padding: "10px 14px" }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Category & Subcategory */}
                    <div className="inv-section-card">
                      <h4 className="inv-section-title">📂 Category & Classification</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                            Category *
                          </label>
                          <select
                            className="inv-select"
                            style={{ width: "100%" }}
                            value={editFormData.productCategory?.[0]?.categoryName || "BASICS"}
                            onChange={handleEditCategoryChange}
                          >
                            <option value="BASICS">BASICS</option>
                            <option value="PLUS SIZES">PLUS SIZES</option>
                            <option value="AMINII LUXE">AMINII LUXE</option>
                            <option value="BEST SELLER">BEST SELLER</option>
                            <option value="NEW ARRIVALS">NEW ARRIVALS</option>
                            {categories
                              .filter(
                                (c) =>
                                  !["BASICS", "PLUS SIZES", "AMINII LUXE", "BEST SELLER", "NEW ARRIVALS"].includes(
                                    c.categoryName
                                  )
                              )
                              .map((c, i) => (
                                <option key={i} value={c.categoryName}>
                                  {c.categoryName}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                            Subcategory
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Kurtas, Co-ords"
                            value={editFormData.productCategory?.[0]?.subCategory?.categoryName || ""}
                            onChange={handleEditSubCategoryChange}
                            className="inv-search-input"
                            style={{ padding: "10px 14px" }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Granular Size Inventory Matrix */}
                    <div className="inv-section-card">
                      <h4 className="inv-section-title">📏 Size Variants & Stock Matrix</h4>
                      <div className="inv-size-builder-list">
                        {editFormData.productSize.map((sz, i) => {
                          const isOOS = Number(sz.stock) === 0 || sz.stock === "" || sz.stock === "0";
                          return (
                            <div key={i} className="inv-size-builder-row-wrap">
                              <div className="inv-size-builder-row">
                                <input
                                  type="text"
                                  placeholder="Size (e.g. S, M, XL, 4XL)"
                                  value={sz.sizeName}
                                  onChange={(e) => handleEditSizeChange(i, "sizeName", e.target.value)}
                                  className="inv-search-input"
                                  style={{ padding: "8px 12px" }}
                                  required
                                />
                                <input
                                  type="number"
                                  placeholder="Stock"
                                  value={sz.stock}
                                  onChange={(e) => handleEditSizeChange(i, "stock", e.target.value)}
                                  className={`inv-search-input ${isOOS ? "inv-input-oos" : ""}`}
                                  style={{ padding: "8px 12px" }}
                                  min="0"
                                  required
                                />
                                <button
                                  type="button"
                                  className="inv-gallery-remove"
                                  style={{ position: "static", width: "32px", height: "32px" }}
                                  onClick={() => handleRemoveEditSize(i)}
                                  disabled={editFormData.productSize.length <= 1}
                                  title="Remove size variant"
                                >
                                  ✕
                                </button>
                              </div>
                              {isOOS && (
                                <div className="inv-size-oos-indicator">
                                  <span className="inv-size-oos-dot"></span> Out of Stock
                                </div>
                              )}
                            </div>
                          );
                        })}

                        <button
                          type="button"
                          className="inv-add-size-btn"
                          onClick={handleAddEditSize}
                        >
                          + Add Size Variant
                        </button>
                      </div>
                    </div>

                    {/* Description & Tags */}
                    <div className="inv-section-card">
                      <h4 className="inv-section-title">📝 Description & Tags</h4>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                          Description *
                        </label>
                        <textarea
                          name="description"
                          rows="4"
                          value={editFormData.description}
                          onChange={handleEditFieldChange}
                          required
                          className="inv-search-input"
                          style={{ padding: "10px 14px", resize: "vertical" }}
                        />
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#a0a0aa" }}>
                          Tags (Comma separated)
                        </label>
                        <input
                          type="text"
                          value={(editFormData.tags || []).join(", ")}
                          onChange={(e) => {
                            const arr = e.target.value.split(",").map((t) => t.trim());
                            setEditFormData((prev) => ({ ...prev, tags: arr }));
                          }}
                          className="inv-search-input"
                          style={{ padding: "10px 14px" }}
                          placeholder="e.g. festive, luxury, premium cotton"
                        />
                      </div>
                    </div>
                  </form>

                  {/* Drawer Footer */}
                  <div className="inv-drawer-footer">
                    <button
                      type="button"
                      className="inv-delete-btn"
                      onClick={handleDeleteProduct}
                      disabled={isSaving}
                    >
                      🗑️ Delete Style
                    </button>
                    <button
                      type="submit"
                      form="edit-product-form"
                      className="inv-save-btn"
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
